import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, ArrowLeft, Loader2, Minus, Plus, Send, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { MoneyValue } from '@/components/pos/MoneyValue';
import { useRefundableLines } from '@/hooks/queries';
import { useRequestRefund, useRequestVoid } from '@/hooks/mutations';
import {
  KIND_LABEL,
  REASON_MAX,
  REASON_MIN,
  approvalErrorMessage,
  isPendingConflict,
  isTicketUsed,
  newIdempotencyKey,
  partialRefundTotal,
  validateRequest,
} from '@/lib/approvals';
import { rupiah } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { ApprovalKind, SaleApprovalRequest } from '@/types/sale';

const KIND_HINT: Record<ApprovalKind, string> = {
  VOID: 'Batalkan seluruh transaksi hari ini (semua tiket belum dipakai).',
  FULL_REFUND: 'Kembalikan seluruh pembayaran (semua tiket belum dipakai).',
  PARTIAL_REFUND: 'Kembalikan sebagian tiket yang belum dipakai.',
};

export interface VoidRefundRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** `id` (uuid) is preferred; the invoiceId is the fallback route key. */
  sale: { id?: string | null; invoiceId: string; totalAmount: number };
  canVoid: boolean;
  canRefund: boolean;
  initialKind?: ApprovalKind;
  /** Called with the created row — the caller shows the pending badge. */
  onSubmitted?: (row: SaleApprovalRequest | null) => void;
  /** A 409 "already pending" — the caller marks the sale as pending. */
  onPendingConflict?: () => void;
}

/**
 * The cashier's side of a void / refund: choose the type, give a reason, (for
 * a partial refund) pick unused tickets, confirm, and send. The supervisor
 * approves in the BO, which executes it; nothing changes on the sale here.
 */
export function VoidRefundRequestDialog({
  open,
  onOpenChange,
  sale,
  canVoid,
  canRefund,
  initialKind,
  onSubmitted,
  onPendingConflict,
}: VoidRefundRequestDialogProps) {
  const kinds = useMemo<ApprovalKind[]>(
    () => [
      ...(canVoid ? (['VOID'] as const) : []),
      ...(canRefund ? (['FULL_REFUND', 'PARTIAL_REFUND'] as const) : []),
    ],
    [canVoid, canRefund],
  );
  const defaultKind = initialKind && kinds.includes(initialKind) ? initialKind : (kinds[0] ?? null);

  const [kind, setKind] = useState<ApprovalKind | null>(defaultKind);
  const [reason, setReason] = useState('');
  const [lineQty, setLineQty] = useState<Record<string, number>>({});
  const [step, setStep] = useState<'form' | 'confirm'>('form');
  const [error, setError] = useState<{ message: string; ticketUsed: boolean } | null>(null);
  const idemKey = useRef<string>(newIdempotencyKey());

  // Fresh state each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setKind(defaultKind);
    setReason('');
    setLineQty({});
    setStep('form');
    setError(null);
    idemKey.current = newIdempotencyKey();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const saleRef = sale.id || sale.invoiceId;
  const partial = kind === 'PARTIAL_REFUND';
  const linesQuery = useRefundableLines(saleRef, open && partial);
  const lines = useMemo(() => linesQuery.data?.lines ?? [], [linesQuery.data]);

  const errors = validateRequest({ kind, reason, lineQty, lines });
  const valid = errors.length === 0;
  const amount = partial ? partialRefundTotal(lines, lineQty) : sale.totalAmount;

  const voidMut = useRequestVoid();
  const refundMut = useRequestRefund();
  const submitting = voidMut.isPending || refundMut.isPending;

  const changeKind = (k: ApprovalKind) => {
    setKind(k);
    setError(null);
    // A changed request body is a different request.
    idemKey.current = newIdempotencyKey();
  };

  const setQty = (id: string, q: number, max: number) =>
    setLineQty((prev) => ({ ...prev, [id]: Math.max(0, Math.min(max, q)) }));

  const submit = async () => {
    if (!valid || !kind) return;
    setError(null);
    const trimmed = reason.trim();
    try {
      const row =
        kind === 'VOID'
          ? await voidMut.mutateAsync({ saleRef, reason: trimmed, idempotencyKey: idemKey.current })
          : await refundMut.mutateAsync({
              saleRef,
              idempotencyKey: idemKey.current,
              input: {
                kind,
                reason: trimmed,
                ...(kind === 'PARTIAL_REFUND'
                  ? {
                      lineRefunds: Object.entries(lineQty)
                        .filter(([, q]) => q > 0)
                        .map(([saleItemId, qty]) => ({ saleItemId, qty })),
                    }
                  : {}),
              },
            });
      toast.success('Permintaan dikirim, menunggu persetujuan supervisor');
      onSubmitted?.(row ?? null);
      onOpenChange(false);
    } catch (err) {
      const ticketUsed = isTicketUsed(err);
      setError({ message: approvalErrorMessage(err), ticketUsed });
      if (isPendingConflict(err)) onPendingConflict?.();
      setStep('form');
    }
  };

  const reasonLen = reason.trim().length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Undo2 className="h-5 w-5 text-primary" />
            Ajukan Void / Refund
          </DialogTitle>
          <DialogDescription>
            <span className="tnum font-semibold text-ink-900">{sale.invoiceId}</span> · total{' '}
            {rupiah(sale.totalAmount)}
          </DialogDescription>
        </DialogHeader>

        {step === 'form' ? (
          <div className="space-y-4">
            <div className="grid gap-2" role="radiogroup" aria-label="Jenis permintaan">
              {kinds.map((k) => (
                <button
                  key={k}
                  type="button"
                  role="radio"
                  aria-checked={kind === k}
                  onClick={() => changeKind(k)}
                  className={cn(
                    'rounded-md border-2 px-3 py-2.5 text-left transition-colors',
                    kind === k
                      ? 'border-primary bg-primary/5 text-ink-900'
                      : 'border-border text-ink-600 hover:border-primary/40',
                  )}
                >
                  <span className="block text-sm font-semibold">{KIND_LABEL[k]}</span>
                  <span className="block text-xs text-muted-foreground">{KIND_HINT[k]}</span>
                </button>
              ))}
            </div>

            {partial && (
              <div className="space-y-2">
                <Label>Tiket yang direfund</Label>
                {linesQuery.isLoading ? (
                  <div className="flex justify-center py-4 text-ink-400">
                    <Loader2 className="h-5 w-5 animate-spin" />
                  </div>
                ) : linesQuery.isError ? (
                  <p className="text-sm text-destructive">
                    {approvalErrorMessage(linesQuery.error)}{' '}
                    <button
                      type="button"
                      className="font-medium underline"
                      onClick={() => void linesQuery.refetch()}
                    >
                      Coba lagi
                    </button>
                  </p>
                ) : lines.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Tidak ada tiket yang bisa direfund.</p>
                ) : (
                  <ul className="max-h-56 space-y-1.5 overflow-y-auto pr-1">
                    {lines.map((l) => {
                      const q = lineQty[l.saleItemId] ?? 0;
                      const none = l.unusedQty <= 0;
                      return (
                        <li
                          key={l.saleItemId}
                          className={cn(
                            'flex items-center gap-3 rounded-md border px-3 py-2',
                            none ? 'border-border bg-secondary/40 opacity-60' : 'border-border',
                          )}
                        >
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-ink-900">{l.productName}</p>
                            <p className="tnum text-xs text-ink-400">
                              {rupiah(l.unitPrice)} · {l.unusedQty} dari {l.qty} belum dipakai
                            </p>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-9 w-9"
                              aria-label={`Kurangi ${l.productName}`}
                              disabled={none || q <= 0}
                              onClick={() => setQty(l.saleItemId, q - 1, l.unusedQty)}
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                            <span className="tnum w-8 text-center font-display font-bold" aria-label={`Jumlah ${l.productName}`}>
                              {q}
                            </span>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-9 w-9"
                              aria-label={`Tambah ${l.productName}`}
                              disabled={none || q >= l.unusedQty}
                              onClick={() => setQty(l.saleItemId, q + 1, l.unusedQty)}
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
                {lines.length > 0 && (
                  <p className="text-right text-sm text-ink-600">
                    Perkiraan refund <MoneyValue value={amount} className="font-semibold" />
                  </p>
                )}
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="approval-reason">Alasan</Label>
              <textarea
                id="approval-reason"
                rows={3}
                maxLength={REASON_MAX}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Jelaskan alasan pengajuan (min. 10 karakter)"
                className="flex w-full rounded-md border-2 border-input bg-card px-4 py-2 text-base text-foreground transition-colors placeholder:text-ink-400 focus-visible:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
              />
              <div className="flex justify-between text-xs">
                <span className={cn(reasonLen > 0 && reasonLen < REASON_MIN ? 'text-destructive' : 'text-ink-400')}>
                  Minimal {REASON_MIN} karakter
                </span>
                <span className="tnum text-ink-400" data-testid="reason-counter">
                  {reasonLen}/{REASON_MAX}
                </span>
              </div>
            </div>

            {error && (
              <div
                role="alert"
                className="space-y-2 rounded-md border-2 border-destructive/25 bg-destructive/5 px-3 py-2.5 text-sm font-medium text-destructive"
              >
                <p>{error.message}</p>
                {error.ticketUsed && canRefund && kind !== 'PARTIAL_REFUND' && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => changeKind('PARTIAL_REFUND')}
                  >
                    Ajukan refund sebagian
                  </Button>
                )}
              </div>
            )}

            <Button
              type="button"
              size="lg"
              className="h-12 w-full"
              disabled={!valid}
              onClick={() => setStep('confirm')}
            >
              Lanjut
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1.5 rounded-md border-2 border-border bg-card px-4 py-3 text-sm">
              <dt className="text-ink-400">Jenis</dt>
              <dd className="font-semibold text-ink-900">{kind ? KIND_LABEL[kind] : '—'}</dd>
              <dt className="text-ink-400">Invoice</dt>
              <dd className="tnum font-semibold text-ink-900">{sale.invoiceId}</dd>
              <dt className="text-ink-400">Nilai</dt>
              <dd>
                <MoneyValue value={amount} className="font-semibold" />
              </dd>
              <dt className="text-ink-400">Alasan</dt>
              <dd className="whitespace-pre-wrap break-words text-ink-900">{reason.trim()}</dd>
            </dl>
            <div className="flex gap-2 rounded-md border-2 border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>
                Permintaan dikirim ke supervisor. Transaksi <strong>belum berubah</strong> sampai
                disetujui.
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="lg"
                className="h-12 gap-2"
                disabled={submitting}
                onClick={() => setStep('form')}
              >
                <ArrowLeft className="h-4 w-4" /> Kembali
              </Button>
              <Button
                type="button"
                size="lg"
                className="h-12 flex-1 gap-2"
                disabled={submitting}
                onClick={() => void submit()}
              >
                {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                Kirim permintaan
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
